/* GrowthOS — Contact form sections (used by addContactsForm.jsx for Create/Edit
   Contact). Same pattern as leadForm.jsx (used by addLead.jsx): section cards in a
   2-col grid + a full-width notes card, config-driven `SECTIONS`, reusing the exact
   the gos-cl-* / gos-dd-* classes — just the field groups match Contact's real schema
   (Contact Information / Account & Ownership / Social Media / Address / Notes)
   instead of Lead's. Keeps the exact handler contract from addContactsForm
   (handleChangeForm / handleChange / handleChangeCheckbox / errormsg / refs).
   "Account Name" is the one searchable field (real accounts list can be large), so
   the local Select adds optional search/infinite-scroll on top of the Lead form's
   plain dropdown, backed by the existing PaginatedDropdown hook. */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveOwners, getGenericMasterFilteredListByKey } from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';
import PaginatedDropdown from '../../shared/component/PaginatedDropdown';
import apiServices from '../../services/service';

const ICONS = {
  contacts: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" /></>),
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  link: <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5" />,
  location: (<><path d="M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></>),
  template: <path d="M4 4h16a1 1 0 0 1 1 1v4H3V5a1 1 0 0 1 1-1ZM3 9h7v11H4a1 1 0 0 1-1-1V9ZM10 9h11v6H10zM10 15h11v4a1 1 0 0 1-1 1H10v-5Z" />,
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  chevD: <path d="M6 9l6 6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
};
const FIc = ({ name, size = 16, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {ICONS[name] || null}
  </svg>
);

/* GrowthOS select (reuses .gos-dd-* dropdown styles); `searchable` adds a search box
   that calls onSearch(text) and an infinite-scroll list that calls onScroll() near
   the bottom — both map straight onto PaginatedDropdown's handleSearch/handleScroll. */
const Select = ({ value, placeholder, options, onChange, disabled, searchable, onSearch, onScroll, loading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const onListScroll = (e) => {
    if (!onScroll) return;
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) onScroll();
  };
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <FIc name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          {searchable ? (
            <div className="gos-dd-search">
              <FIc name="search" size={14} />
              <input autoFocus placeholder="Search…" onChange={(e) => onSearch && onSearch(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list" onScroll={onScroll ? onListScroll : undefined}>
            {loading && options.length === 0 ? <div className="gos-dd-empty">Loading…</div> : null}
            {!loading && options.length === 0 ? <div className="gos-dd-empty">No options</div> : null}
            {options.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <FIc name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
            {loading && options.length > 0 ? <div className="gos-dd-empty">Loading more…</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ContactForm = ({ formData, handleChangeForm, handleChange, errormsg, handleChangeCheckbox, refs }) => {
  const owner = useSelector(getActiveOwners);
  const leadSourceDropdown = useSelector(getGenericMasterFilteredListByKey('LeadSource'));
  const salutation = useSelector(getGenericMasterFilteredListByKey('Salutation'));
  const contactPersonRole = useSelector(getGenericMasterFilteredListByKey('ContactPersonRole'));

  const {
    options: accounts,
    isLoading: isAccountLoading,
    handleSearch: handleAccountSearch,
    handleScroll: handleAccountScroll,
  } = PaginatedDropdown({
    fetchFn: apiServices.getAccountsDropdown,
    responseKey: 'accountList',
  });

  const opt = (list, vKey, lKey, fmt) =>
    (list || []).map((o) => ({ value: o[vKey], label: fmt ? fmt(o[lKey]) : o[lKey] }));

  // field config: text | select | select-search | textarea
  const SECTIONS = [
    {
      id: 'contact', icon: 'contacts', title: 'Contact Information', sub: "Primary details for this person", optout: true,
      fields: [
        { label: 'Salutation', name: 'salutationId', type: 'select', display: 'salutation', options: opt(salutation, 'id', 'displayName') },
        { label: 'First Name', name: 'firstName' },
        { label: 'Middle Name', name: 'middleName' },
        { label: 'Last Name', name: 'lastName', req: true, ref: 'lastName' },
        { label: 'Title', name: 'title', full: true },
        { label: 'Email', name: 'email' },
        { label: 'Secondary Email', name: 'secondaryEmail' },
        { label: 'Department', name: 'department' },
        { label: 'Phone', name: 'phone' },
        { label: 'Mobile', name: 'mobile' },
        { label: 'Reporting To', name: 'reportingTo' },
        { label: 'Assistant', name: 'assistant' },
        { label: 'Assistant Phone', name: 'assistantPhone' },
        { label: 'Contact Person Role', name: 'contactPersonRoleId', type: 'select', display: 'contactPersonRole', options: opt(contactPersonRole, 'id', 'displayName') },
      ],
    },
    {
      id: 'account', icon: 'building', title: 'Account & Ownership', sub: 'Company link & source',
      fields: [
        { label: 'Account Name', name: 'accountId', type: 'select-search', display: 'account', full: true,
          options: (accounts || []).map((a) => ({ value: a.id, label: a.name })), loading: isAccountLoading,
          onSearch: handleAccountSearch, onScroll: handleAccountScroll },
        { label: 'Contact Owner', name: 'ownerId', type: 'select', display: 'owner', options: opt(owner, 'userId', 'display_name', NameFormatter) },
        { label: 'Lead Source', name: 'leadSourceId', type: 'select', display: 'leadSource', req: true, ref: 'leadSourceId', options: opt(leadSourceDropdown, 'id', 'displayName') },
      ],
    },
    {
      id: 'social', icon: 'link', title: 'Social Media', sub: 'Public profiles',
      fields: [
        { label: 'LinkedIn', name: 'linkedin', full: true },
        { label: 'Twitter', name: 'twitter', full: true },
        { label: 'Facebook', name: 'facebook', full: true },
        { label: 'Skype ID', name: 'skypeId', full: true },
      ],
    },
    {
      id: 'address', icon: 'location', title: 'Address Information', sub: 'Mailing location',
      fields: [
        { label: 'Street', name: 'street', full: true },
        { label: 'City', name: 'city' },
        { label: 'State', name: 'state' },
        { label: 'Zip Code', name: 'zipCode' },
        { label: 'Country', name: 'country' },
      ],
    },
    {
      id: 'notes', icon: 'template', title: 'Notes & Tech Details', sub: 'Context for the sales team',
      fields: [
        { label: 'Description', name: 'description', type: 'textarea', full: true },
        { label: 'Technology Stack', name: 'technologyStack', type: 'textarea', full: true },
      ],
    },
  ];

  const renderField = (fl) => {
    const err = errormsg?.[fl.name];
    let control;
    if (fl.type === 'select' || fl.type === 'select-search') {
      control = (
        <Select
          value={formData?.[fl.display] || ''}
          placeholder={'Select ' + fl.label}
          options={fl.options}
          onChange={(o) => handleChange(o, fl.name)}
          searchable={fl.type === 'select-search'}
          onSearch={fl.onSearch}
          onScroll={fl.onScroll}
          loading={fl.loading}
        />
      );
    } else if (fl.type === 'textarea') {
      control = (
        <textarea
          className={'gos-cl-input gos-cl-textarea' + (err ? ' err' : '')}
          name={fl.name}
          rows={4}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    } else {
      control = (
        <input
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    }
    return (
      <div className={'gos-cl-field' + (fl.full ? ' gos-cl-field-full' : '')} key={fl.name} ref={fl.ref ? refs?.[fl.ref] : undefined}>
        <label className="gos-cl-label">
          {fl.label}
          {fl.req ? <span className="gos-cl-req">*</span> : null}
        </label>
        {control}
        {err ? <span className="gos-cl-err">{err}</span> : null}
      </div>
    );
  };

  const secCount = (s) => s.fields.filter((fl) => { const v = formData?.[fl.name]; return v !== null && v !== undefined && String(v).trim() !== ''; }).length;

  const optedOut = !!formData?.emailOptOut;
  const optoutRow = (
    <div className="gos-cl-optrow" key="optout">
      <span className="gos-cl-optrow-ic" style={optedOut ? { background: 'var(--gos-amber-bg)', color: 'var(--gos-amber-fg)' } : null}>
        <FIc name="mail" size={16} />
      </span>
      <div className="gos-cl-optrow-meta">
        <div className="gos-cl-optrow-title">Email Opt Out</div>
        <div className="gos-cl-optrow-sub">{optedOut ? 'Excluded from email campaigns' : 'Receives marketing & sales emails'}</div>
      </div>
      <button
        type="button"
        className={'gos-cl-toggle' + (optedOut ? ' on' : '')}
        onClick={() => handleChangeCheckbox({ target: { name: 'emailOptOut', checked: !optedOut } })}
        aria-pressed={optedOut}
      >
        <span className="gos-cl-toggle-knob" />
      </button>
    </div>
  );

  const section = (s) => {
    const n = secCount(s);
    const total = s.fields.length;
    return (
      <div className="gos-lr-card gos-cl-sec" id={'sec-' + s.id} key={s.id}>
        <div className="gos-cl-sec-head">
          <span className="gos-cl-sec-ic"><FIc name={s.icon} size={17} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="gos-cl-sec-title">{s.title}</div>
            <div className="gos-cl-sec-sub">{s.sub}</div>
          </div>
          <span className={'gos-cl-count' + (n === total && n > 0 ? ' done' : '')}>{n}/{total}</span>
        </div>
        <div className="gos-cl-sec-body">
          {s.fields.map(renderField)}
          {s.optout ? optoutRow : null}
        </div>
      </div>
    );
  };

  const byId = (id) => SECTIONS.find((s) => s.id === id);

  return (
    <>
      <div className="gos-cl3-grid">
        <div className="gos-cl3-col">{section(byId('contact'))}</div>
        <div className="gos-cl3-col">{section(byId('account'))}{section(byId('social'))}{section(byId('address'))}</div>
      </div>
      <div style={{ marginTop: 18 }}>{section(byId('notes'))}</div>
    </>
  );
};

export default ContactForm;
